import { useAuthStore } from '../../mia-auth/store/auth-store'
import { appConfig } from '../config/app-config'
import { buildAdminApiUrl } from '../config/api-base-url'
import { adminRoutes } from './admin-routes'
import { ApiError, type ApiErrorPayload } from './types'

const API_BASE_URL = appConfig.apiBaseUrl
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  tenantId?: string
  environmentId?: string
  idempotencyKey?: string
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return ''
  const prefix = `${encodeURIComponent(name)}=`
  const cookie = document.cookie.split('; ').find((item) => item.startsWith(prefix))
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : ''
}

function parsePayload(text: string) {
  if (!text.trim()) return null
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

function createCorrelationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `mia-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function fallbackError(status: number, path: string, correlationId: string): ApiErrorPayload {
  if (status === 404) {
    return {
      code: 'ADMIN_ROUTE_NOT_FOUND',
      message: 'Este recurso administrativo não está disponível. Verifique se frontend e backend estão na mesma versão.',
      correlationId,
      details: { path },
    }
  }
  if (status === 405) {
    return {
      code: 'ADMIN_METHOD_NOT_ALLOWED',
      message: 'A operação solicitada não é permitida para este recurso administrativo.',
      correlationId,
      details: { path },
    }
  }
  if (status >= 500) {
    return {
      code: 'ADMIN_SERVICE_ERROR',
      message: 'O serviço administrativo está temporariamente indisponível. Tente novamente.',
      correlationId,
      details: { path },
    }
  }
  return {
    code: 'HTTP_ERROR',
    message: `Não foi possível concluir a operação. Código HTTP ${status}.`,
    correlationId,
    details: { path },
  }
}

function normalizeErrorPayload(payload: unknown, status: number, path: string, correlationId: string) {
  if (payload && typeof payload === 'object' && 'code' in payload && 'message' in payload) {
    const candidate = payload as ApiErrorPayload
    return { ...candidate, correlationId: candidate.correlationId || correlationId }
  }
  return fallbackError(status, path, correlationId)
}

function isCSRFValidationFailure(payload: unknown) {
  return Boolean(payload && typeof payload === 'object' && 'code' in payload && payload.code === 'CSRF_VALIDATION_FAILED')
}

async function fetchCSRFToken(correlationId: string) {
  const response = await fetch(buildAdminApiUrl(API_BASE_URL, adminRoutes.auth.csrf), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Correlation-ID': correlationId,
    },
    credentials: 'include',
    cache: 'no-store',
  })
  if (!response.ok) return ''
  const payload = parsePayload(await response.text())
  if (!payload || typeof payload !== 'object' || !('csrfToken' in payload) || typeof payload.csrfToken !== 'string') {
    return ''
  }
  useAuthStore.getState().setCSRFToken(payload.csrfToken)
  return payload.csrfToken
}

export async function publicAssetRequest(requestUrl: string, options: RequestInit = {}) {
  return fetch(requestUrl, {
    ...options,
    credentials: 'omit',
    cache: options.cache ?? 'no-store',
  })
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    body: requestBody,
    tenantId: tenantIdOverride,
    environmentId: environmentIdOverride,
    idempotencyKey,
    headers: requestHeaders,
    ...requestInit
  } = options
  const method = requestInit.method?.toUpperCase() ?? 'GET'
  const unsafe = !SAFE_METHODS.has(method)
  const correlationId = new Headers(requestHeaders).get('X-Correlation-ID') || createCorrelationId()
  const requestUrl = buildAdminApiUrl(API_BASE_URL, path)
  const isFormData = typeof FormData !== 'undefined' && requestBody instanceof FormData
  const body = requestBody === undefined ? undefined : isFormData ? requestBody : JSON.stringify(requestBody)
  const tenantId = tenantIdOverride ?? useAuthStore.getState().tenantId
  const environmentId = environmentIdOverride ?? useAuthStore.getState().environmentId
  const requestIdempotencyKey = idempotencyKey ?? createCorrelationId()

  let csrfToken = new Headers(requestHeaders).get('X-CSRF-Token')
    || useAuthStore.getState().csrfToken
    || readCookie('mia_every_csrf')
  if (unsafe && path !== adminRoutes.auth.login && !csrfToken) {
    csrfToken = await fetchCSRFToken(correlationId)
  }

  const execute = async (token: string) => {
    const headers = new Headers(requestHeaders)
    headers.set('Accept', 'application/json')
    headers.set('X-Correlation-ID', correlationId)
    if (body && !isFormData) {
      headers.set('Content-Type', 'application/json')
    }
    if (tenantId) {
      headers.set('X-Tenant-ID', tenantId)
    }
    if (environmentId) {
      headers.set('X-Environment-ID', environmentId)
    }
    if (unsafe) {
      if (token) headers.set('X-CSRF-Token', token)
      headers.set('X-Idempotency-Key', requestIdempotencyKey)
    }
    return fetch(requestUrl, {
      ...requestInit,
      method,
      body,
      headers,
      credentials: 'include',
      cache: requestInit.cache ?? 'no-store',
    })
  }

  let response: Response
  try {
    response = await execute(csrfToken)
  } catch {
    throw new ApiError(0, {
      code: 'API_UNREACHABLE',
      message: 'Não foi possível conectar à API. Verifique o backend e a configuração do ambiente.',
      correlationId,
      details: { path },
    })
  }

  let responseText = response.status === 204 ? '' : await response.text()
  let payload = parsePayload(responseText)
  if (unsafe && path !== adminRoutes.auth.login && response.status === 403 && isCSRFValidationFailure(payload)) {
    const refreshedToken = await fetchCSRFToken(correlationId)
    if (refreshedToken) {
      response = await execute(refreshedToken)
      responseText = response.status === 204 ? '' : await response.text()
      payload = parsePayload(responseText)
    }
  }

  const responseCorrelationId = response.headers.get('X-Correlation-ID') || correlationId
  if (response.status === 204) {
    return undefined as T
  }
  if (!response.ok) {
    throw new ApiError(response.status, normalizeErrorPayload(payload, response.status, path, responseCorrelationId))
  }
  if (payload === null) {
    throw new ApiError(response.status, {
      code: 'INVALID_API_RESPONSE',
      message: 'A API retornou uma resposta inválida. Atualize a página e tente novamente.',
      correlationId: responseCorrelationId,
      details: { path },
    })
  }
  return payload as T
}

type UploadRequestOptions = {
  onProgress?: (progress: number) => void
  signal?: AbortSignal
  tenantId?: string
  environmentId?: string
  idempotencyKey?: string
}

export async function apiUploadRequest<T>(path: string, body: FormData, options: UploadRequestOptions = {}): Promise<T> {
  const correlationId = createCorrelationId()
  const tenantId = options.tenantId ?? useAuthStore.getState().tenantId
  const environmentId = options.environmentId ?? useAuthStore.getState().environmentId
  const idempotencyKey = options.idempotencyKey ?? createCorrelationId()
  let csrfToken = useAuthStore.getState().csrfToken || readCookie('mia_every_csrf')
  if (!csrfToken) csrfToken = await fetchCSRFToken(correlationId)

  const execute = (token: string, allowCSRFRefresh: boolean): Promise<T> => new Promise<T>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('Upload cancelado', 'AbortError'))
      return
    }
    const request = new XMLHttpRequest()
    const abort = () => request.abort()
    request.open('POST', buildAdminApiUrl(API_BASE_URL, path))
    request.withCredentials = true
    request.responseType = 'text'
    request.setRequestHeader('Accept', 'application/json')
    request.setRequestHeader('X-Correlation-ID', correlationId)
    request.setRequestHeader('X-Idempotency-Key', idempotencyKey)
    if (token) request.setRequestHeader('X-CSRF-Token', token)
    if (tenantId) request.setRequestHeader('X-Tenant-ID', tenantId)
    if (environmentId) request.setRequestHeader('X-Environment-ID', environmentId)
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && options.onProgress) options.onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
    }
    request.onerror = () => reject(new ApiError(0, {
      code: 'API_UNREACHABLE',
      message: 'Não foi possível conectar ao serviço de imagens.',
      correlationId,
      details: { path },
    }))
    request.onabort = () => reject(new DOMException('Upload cancelado', 'AbortError'))
    request.onload = () => {
      const payload = parsePayload(request.responseText)
      const responseCorrelationId = request.getResponseHeader('X-Correlation-ID') || correlationId
      if (allowCSRFRefresh && request.status === 403 && isCSRFValidationFailure(payload)) {
        void fetchCSRFToken(correlationId).then((refreshedToken) => {
          if (!refreshedToken) {
            reject(new ApiError(request.status, normalizeErrorPayload(payload, request.status, path, responseCorrelationId)))
            return
          }
          resolve(execute(refreshedToken, false))
        }).catch(reject)
        return
      }
      if (request.status < 200 || request.status >= 300) {
        reject(new ApiError(request.status, normalizeErrorPayload(payload, request.status, path, responseCorrelationId)))
        return
      }
      if (payload === null) {
        reject(new ApiError(request.status, {
          code: 'INVALID_API_RESPONSE',
          message: 'O serviço de imagens retornou uma resposta inválida.',
          correlationId: responseCorrelationId,
          details: { path },
        }))
        return
      }
      resolve(payload as T)
    }
    options.signal?.addEventListener('abort', abort, { once: true })
    request.onloadend = () => options.signal?.removeEventListener('abort', abort)
    request.send(body)
  })

  return execute(csrfToken, true)
}
